
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ProfileTabs from "@/components/profile/ProfileTabs";
import LoadingSpinner from "@/components/profile/LoadingSpinner";
import { useProfile } from "@/hooks/useProfile";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const Profile = () => {
  const { 
    profile, 
    loading, 
    uploading, 
    activeTab, 
    setActiveTab, 
    handleChange, 
    handleSubmit,
    handleImageUpload,
    isProfileComplete,
    autoSaved,
    savingState
  } = useProfile();
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [profileTransition, setProfileTransition] = useState(false);
  
  // Set the default active tab to dashboard if profile is complete
  useEffect(() => {
    if (isProfileComplete && activeTab === "profile") {
      setActiveTab("dashboard");
    }
  }, [isProfileComplete]);
  
  // Add a transition delay when profile is complete to show the user the status
  useEffect(() => {
    if (isProfileComplete && !loading && !uploading && !isSaving) {
      setProfileTransition(true);
      const timer = setTimeout(() => {
        navigate("/dashboard");
      }, 2500);  // Give user time to see the completion message
      
      return () => clearTimeout(timer);
    }
  }, [isProfileComplete, loading, uploading, isSaving, navigate]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent form submission
    
    if (isProfileComplete) {
      console.log("Profile is complete, navigating to dashboard");
      setProfileTransition(true);
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
      return;
    }
    
    setIsSaving(true);
    console.log("Handling form submit in Profile component");
    try {
      const success = await handleSubmit(e);
      console.log("Profile save success:", success);
      
      if (success) {
        console.log("Profile updated successfully");
        toast({
          title: "Profile updated",
          description: "Your profile has been saved successfully"
        });
        
        if (isProfileComplete) {
          console.log("Profile is complete, navigating to dashboard");
          setProfileTransition(true);
          setTimeout(() => {
            navigate("/dashboard");
          }, 2000); 
        } else {
          console.log("Profile updated but still incomplete");
        }
      } else {
        console.log("Profile update failed, staying on profile page");
      }
    } catch (error) {
      console.error("Error in form submission:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const getMissingFieldsList = () => {
    const missing = [];
    
    if (!profile.full_name?.trim()) missing.push("Full name");
    if (!profile.username?.trim()) missing.push("Username");
    if (!profile.bio?.trim()) missing.push("Bio");
    if (!profile.interests?.trim()) missing.push("Interests");
    if (!profile.avatar_url?.trim()) missing.push("Profile picture");
    
    return missing;
  };

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {!isProfileComplete && (
            <Alert className="mb-6 bg-amber-50 border-amber-200">
              <AlertTitle className="text-amber-800">Complete your profile</AlertTitle>
              <AlertDescription className="text-amber-700">
                Please complete your profile before accessing the dashboard. 
                <ul className="mt-2 list-disc list-inside">
                  {getMissingFieldsList().map((field, index) => (
                    <li key={index}>{field}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <ProfileTabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                profile={profile}
                handleChange={handleChange}
                handleSubmit={handleFormSubmit}
                uploading={uploading || isSaving}
                autoSaved={autoSaved}
                handleImageUpload={handleImageUpload}
                savingState={savingState}
                isProfileComplete={isProfileComplete}
              />
              
              {isProfileComplete && (
                <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-lg">Ready to Explore?</h3>
                      <p className="text-muted-foreground">Your profile is complete! Click the button to access your personalized dashboard.</p>
                    </div>
                    {profileTransition ? (
                      <div className="flex items-center gap-2 text-primary">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Taking you to your dashboard...</span>
                      </div>
                    ) : (
                      <Button 
                        onClick={() => {
                          setProfileTransition(true);
                          setTimeout(() => navigate("/dashboard"), 1000);
                        }}
                        className="flex items-center gap-2 w-full sm:w-auto"
                      >
                        Go to Dashboard <ArrowRight className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Profile;
